// Single source of truth for every fee quoted in the quote flow.
//
// QuoteCoverage (via StateCoveragesForm/CoveragePricing), QuoteQuote and
// QuotePayment all derive their fee amounts AND their fee-disclosure copy from
// this module, so the three screens cannot drift apart the way they did when
// each kept its own table.
//
// ── The fee model ────────────────────────────────────────────────────────────
//   Policy Fee              $5.00 per month
//   Mileage Monitoring Fee  $5.00 per month, only where mileage is billed
//                           (an Unlimited policy does not carry it)
//   Credit Card Fee         per state, charged once per payment
//
// A monthly plan pays all three every month. A six-month plan accrues six
// months of the Policy Fee, but — because it is a single payment for the whole
// term — only ONE credit card fee, and the Mileage Monitoring Fee is waived
// entirely.

const POLICY_FEE_MONTHLY = 5.0;
const MILEAGE_MONITORING_FEE_MONTHLY = 5.0;
const POLICY_TERM_MONTHS = 6;

const CREDIT_CARD_FEE_DEFAULT = 4.95;
// CO / TX / UT quote a $5.00 credit card fee; FL charges none at all.
const CREDIT_CARD_FEE_BY_STATE = {
  CO: 5.0,
  TX: 5.0,
  UT: 5.0,
  FL: 0
};

const round = value => Math.round((value + Number.EPSILON) * 100) / 100;

const currency = value => '$' + round(value).toFixed(2);

// "a, b and c" — matches the fee copy the quote flow has always used.
const sentenceList = parts => {
  if (parts.length < 2) {
    return parts.join('');
  }
  return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
};

export function creditCardFee(state) {
  const key = (state || '').toUpperCase();
  return Object.prototype.hasOwnProperty.call(CREDIT_CARD_FEE_BY_STATE, key)
    ? CREDIT_CARD_FEE_BY_STATE[key]
    : CREDIT_CARD_FEE_DEFAULT;
}

export function mileageMonitoringFee(isUnlimited) {
  return isUnlimited ? 0 : MILEAGE_MONITORING_FEE_MONTHLY;
}

export function policyFee() {
  return POLICY_FEE_MONTHLY;
}

// Fees carried by a single monthly payment (and by the monthly down payment).
export function monthlyFees(state, isUnlimited) {
  return round(
    POLICY_FEE_MONTHLY +
      mileageMonitoringFee(isUnlimited) +
      creditCardFee(state)
  );
}

// Six months of monthly fees — a monthly plan's fee cost across the term.
export function policyTermFees(state, isUnlimited) {
  return round(POLICY_TERM_MONTHS * monthlyFees(state, isUnlimited));
}

// The six-month paid-in-full plan: six months of Policy Fee, a single credit
// card fee for the one payment, and no Mileage Monitoring Fee — it is waived
// when the term is paid in advance. Unlimited makes no difference here, since
// the Mileage Monitoring Fee is already off.
export function paidInFullFees(state) {
  return round(POLICY_TERM_MONTHS * POLICY_FEE_MONTHLY + creditCardFee(state));
}

const TRANSPARENCY =
  'We are committed to providing you with great service and 100% transparent pricing.';

export function monthlyFeeTooltip(state, isUnlimited) {
  const parts = ['a Policy Fee of ' + currency(POLICY_FEE_MONTHLY)];
  if (!isUnlimited) {
    parts.push(
      'a Mileage Monitoring Fee of ' + currency(MILEAGE_MONITORING_FEE_MONTHLY)
    );
  }
  const cardFee = creditCardFee(state);
  if (cardFee) {
    parts.push('a Credit Card Fee of ' + currency(cardFee));
  }
  return (
    TRANSPARENCY + ' Your monthly payment includes ' + sentenceList(parts) + '.'
  );
}

export function paidInFullFeeTooltip(state) {
  const parts = [
    'a Policy Fee of ' +
      currency(POLICY_FEE_MONTHLY * POLICY_TERM_MONTHS) +
      ' (' +
      currency(POLICY_FEE_MONTHLY) +
      ' per month)'
  ];
  const cardFee = creditCardFee(state);
  if (cardFee) {
    parts.push('a one-time Credit Card Fee of ' + currency(cardFee));
  }
  return (
    TRANSPARENCY +
    ' Your one-time payment covers the full six-month term and includes ' +
    sentenceList(parts) +
    '. The Mileage Monitoring Fee is waived when the six-month premium is paid' +
    ' in advance.'
  );
}
